import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Lock, Gauge, Wrench, ShieldCheck } from "lucide-react";

export const Browser3D = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateY = useTransform(sx, [-0.5, 0.5], reduce ? [0, 0] : [-9, 9]);
  const rotateX = useTransform(sy, [-0.5, 0.5], reduce ? [0, 0] : [7, -7]);

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative" style={{ perspective: 1200 }} data-testid="hero-3d-visual">
      <div className="absolute -inset-10 bg-[radial-gradient(ellipse_at_center,_rgba(0,85,255,0.08),_transparent_65%)] blur-2xl" aria-hidden="true" />
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        {/* Main browser window */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(2,6,23,0.12)]" style={{ transform: "translateZ(30px)" }}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            <div className="ml-3 flex-1 flex items-center gap-2 bg-slate-100 rounded-md px-3 py-1.5">
              <Lock className="w-3 h-3 text-emerald-500" />
              <span className="font-mono2 text-[11px] text-slate-500">yourbusiness.co.uk</span>
            </div>
          </div>
          <div className="p-5 sm:p-7">
            <div className="h-3 w-24 rounded-full bg-cyan-500/60 mb-4" />
            <div className="h-6 sm:h-8 w-4/5 rounded-lg bg-slate-900 mb-2.5" />
            <div className="h-6 sm:h-8 w-3/5 rounded-lg bg-slate-300 mb-5" />
            <div className="h-3 w-full rounded-full bg-slate-100 mb-2" />
            <div className="h-3 w-5/6 rounded-full bg-slate-100 mb-6" />
            <div className="flex gap-3 mb-7">
              <div className="h-9 w-32 rounded-full bg-[#0055FF] glow-blue" />
              <div className="h-9 w-28 rounded-full border border-slate-300" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="h-8 rounded-lg bg-gradient-to-br from-[#0055FF]/25 to-cyan-400/25 mb-2.5" />
                  <div className="h-2 w-4/5 rounded-full bg-slate-200 mb-1.5" />
                  <div className="h-2 w-3/5 rounded-full bg-slate-100" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating chips */}
        <div className="absolute -left-4 sm:-left-10 top-16 float-slow" style={{ transform: "translateZ(70px)" }}>
          <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.07)]">
            <span className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-emerald-600" /></span>
            <div><p className="text-[11px] font-semibold text-slate-900 leading-tight">SSL Secure</p><p className="text-[10px] text-slate-500">Hosting active</p></div>
          </div>
        </div>
        <div className="absolute -right-3 sm:-right-8 top-1/3 float-slower" style={{ transform: "translateZ(90px)" }}>
          <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.07)]">
            <span className="w-8 h-8 rounded-lg bg-[#F0F4FF] flex items-center justify-center"><Gauge className="w-4 h-4 text-[#0055FF]" /></span>
            <div><p className="text-[11px] font-semibold text-slate-900 leading-tight">98 PageSpeed</p><p className="text-[10px] text-slate-500">Optimised</p></div>
          </div>
        </div>
        <div className="absolute -bottom-5 left-1/4 float-slow" style={{ transform: "translateZ(80px)", animationDelay: "0.7s" }}>
          <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.07)]">
            <span className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center"><Wrench className="w-4 h-4 text-cyan-600" /></span>
            <div><p className="text-[11px] font-semibold text-slate-900 leading-tight">Maintained</p><p className="text-[10px] text-slate-500">Backed up today</p></div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
